import { useCallback, useEffect, useState } from 'react';
import { getEmailsListRequest, getEmailsTypesRequest } from 'redux/reducers/emails/reducer';
import { useDispatch, useSelector } from 'react-redux';
import { getEmailsListData, getEmailsListLoading } from 'redux/reducers/emails/selectors';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { saveUrl3 } from 'redux/reducers/auth/reducer';
import { PATHS } from 'routes/constants';
import { PAGE_SIZE } from 'services/constants/env';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { TFilters, TOrderBy } from 'redux/types';
import { TEmailListItem } from 'redux/reducers/emails/types';
import { Box } from '@mui/material';
import { tableStructure } from '../tableStructure';
import { getFiltersFromNavigateParams } from '../../../../../utils/FilterParams';
import { TypeHandleFilter } from '../TypeHandleFilter';
import { CommunicationHistoryTabs } from '../CommunicationHistoryTabs';
import { TEmailHistoryProps } from './types';

export const EmailHistory = ({ activeTab, onTabChange }: TEmailHistoryProps) => {
	const { emId } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const emailsList = useSelector(getEmailsListData);
	const emailsListLoading = useSelector(getEmailsListLoading);
	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TEmailListItem>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const { page } = queryString.parse(search);

	const navigateParamsWithoutPage = getFiltersFromNavigateParams(
		currentTableStructure,
		navigateParams,
	) as TFilters;

	useEffect(() => {
		dispatch(getEmailsTypesRequest());
	}, [dispatch]);

	useEffect(() => {
		if (!emId) return;
		const orderBy: TOrderBy[] = [];
		const sortIndex = navigateParams?.sortIndex;
		if (sortIndex !== undefined && sortIndex !== null && currentTableStructure[Number(sortIndex)]) {
			orderBy.push({
				fieldName: currentTableStructure[Number(sortIndex)]?.selector as string,
				direction:
					navigateParams?.sortDirection && String(navigateParams.sortDirection) === 'desc'
						? ESortDirection.DESC
						: ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });
		navigateParamsWithoutPage.push({ fieldName: 'emId', value: String(emId ?? ''), operator: '=' });
		if (navigateParams?.SearchTypeHandle) {
			navigateParamsWithoutPage.push({
				fieldName: 'typeHandle',
				value: navigateParams.SearchTypeHandle as string,
				operator: '=',
			});
		}

		dispatch(
			getEmailsListRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(navigateParams?.search ?? ''),
				filters: navigateParamsWithoutPage,
				orderBy,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search, emId]);

	const handleLink = useCallback(
		(id: number) => {
			dispatch(saveUrl3(window.location.pathname + window.location.search));
			const path = PATHS.EM_MANAGEMENT_COMMUNICATION_HISTORY_DETAIL_PAGE.replace(
				':emId',
				String(emId ?? ''),
			).replace(':emailId', String(id));
			const item = emailsList?.data?.find((row) => row.id === id);
			navigate(path, { state: item ? { email: item } : undefined });
		},
		[dispatch, emId, emailsList?.data, navigate],
	);

	return (
		<EasTableWithFilter<TEmailListItem>
			tableStructure={currentTableStructure}
			items={emailsList}
			itemsIsLoading={emailsListLoading}
			handleAction={() => {}}
			handleLink={handleLink}
			skeletonRows={[1, 1, 1, 1, 1, 1, 1, 1]}
			onTableStructureChange={handleTableStructureChange}
			filterChildrenToEnd={
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						padding: '0 8px',
						gap: 2,
					}}
				>
					<TypeHandleFilter />
				</Box>
			}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: '0 8px',
				}}
			>
				<CommunicationHistoryTabs value={activeTab} onChange={onTabChange} />
			</Box>
		</EasTableWithFilter>
	);
};
